import React, { useEffect, useState } from 'react';
import { EmotionProvider as EmotionThemeProvider } from '@tanium/react-emotion';
import { ThemeProvider as TaniumThemeProvider } from '@tanium/react-theme-context';
import { bluestar } from '@tanium/theme-data';
import { PermissionsResponse } from '../../types';
import { PermissionsContext } from '../../hooks/useUserPermissions';
import { AuthenticatedClient } from '../../api/authenticatedClient';
import { MockAuthenticatedClient } from '../../api/mockAuthenticatedClient';
import { AuthenticatedApiContext } from '../../api/useApiClient';
import { useMockApiData } from '../../api/index';
import { authenticatedApiClient } from '../../apiClient';
import { AuthContext } from '../../auth';
import { AuthCallbacks } from '../../auth/Auth';
import { Shell } from '../Shell/Shell';
import { FullPageSpinner } from '../FullPageSpinner';
import { TenantProvider } from '../../hooks/useTenant';

interface Props {
  auth: AuthCallbacks;
}

const getAuthClient = (auth: AuthCallbacks) => {
  /* istanbul ignore next */
  if (useMockApiData) {
    // TODO(AMP-214): Remove mock data when we are ready
    return new MockAuthenticatedClient();
  }
  return new AuthenticatedClient(authenticatedApiClient(auth));
};

export const AuthenticatedArea: React.VFC<Props> = ({ auth }) => {
  const authClient = getAuthClient(auth);
  const [permissions, setPermissions] = useState<PermissionsResponse>({ permissions: [] });

  useEffect(() => {
    async function getPermissions() {
      const data = await authClient.getPermissions();
      setPermissions(data);
    }
    if (permissions.permissions.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      getPermissions();
    }
  }, [permissions.permissions.length]);

  return permissions.permissions.length ? (
    <EmotionThemeProvider theme={bluestar}>
      <TaniumThemeProvider theme={bluestar}>
        <AuthContext.Provider value={auth}>
          <AuthenticatedApiContext.Provider value={authClient}>
            <PermissionsContext.Provider value={permissions}>
              <TenantProvider>
                <Shell />
              </TenantProvider>
            </PermissionsContext.Provider>
          </AuthenticatedApiContext.Provider>
        </AuthContext.Provider>
      </TaniumThemeProvider>
    </EmotionThemeProvider>
  ) : (
    <FullPageSpinner />
  );
};
